// components/UpcomingSessionCard.tsx
import React, { useMemo, useState } from "react";
import { Stack, Typography, Skeleton, Box } from "@mui/material";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import { useGetStageBookings } from "../../../../services/stagemode_api/venue/useGetStageBookings";
import { useAuth } from "../../../../hooks/useAuth";
import StageSessionCard from "./StageSessionCard";
import CustomTab from "../../../common/CustomTab";

dayjs.extend(minMax);

export interface StageBooking {
  id: number;
  stage_card_id: number;
  talent_id: number;
  user_id: number;
  channel_name: string;
  venue_name: string;
  venue_size: string;
  venue_location: string;
  message: string;
  contact_email: string;
  start_date_time: string;
  end_date_time: string;
  payment_status: "Paid" | "Unpaid" | "Failed";
  status: "Ongoing" | "Pending" | "Completed" | "Cancelled" | "Scheduled";
  first_reminder_sent?: number;
  talent?: {
    username?: string;
  };
  stagecard?: {
    title?: string;
    short_description?: string;
    primary_thumbnail?: string;
    banner_title?: string;
  };
}

// ✅ Tab config
const tabs = [
  { label: "Accepted", value: "accepted" },
  { label: "Ongoing", value: "ongoing" },
  { label: "Completed", value: "completed" },
  { label: "Requested", value: "requested" },
  { label: "Cancelled", value: "cancelled" },
];

const UserStageSessions: React.FC = () => {
  const { userInfo } = useAuth();
  const { data, isLoading, error } = useGetStageBookings({
    key: ["stage-bookings", userInfo?.id],
  });

  const [filter, setFilter] = useState<
    "accepted" | "ongoing" | "completed" | "requested" | "cancelled"
  >("accepted");

  const filteredBookings = useMemo(() => {
    if (!data?.data) return [];

    // Filter bookings for the current user
    const userBookings = data.data.filter((b: StageBooking) => b.user_id === userInfo?.id);

    return userBookings.filter((b: StageBooking) => {
      if (filter === "accepted") {
        return b?.status === "Scheduled";
      }

      if (filter === "ongoing") {
        return b?.status === "Ongoing";
      }

      if (filter === "completed") {
        return b?.status === "Completed";
      }

      if (filter === "requested") {
        return b?.status === "Pending";
      }

      if (filter === "cancelled") {
        return b?.status === "Cancelled";
      }

      return false;
    });
  }, [data, filter, userInfo?.id]);

  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[...Array(3)].map((_, idx) => (
          <Skeleton key={idx} variant="rounded" height={120} />
        ))}
      </Stack>
    );
  }

  if (error) {
    return <Typography color="error">Failed to load sessions.</Typography>;
  }

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mt: 4,
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <CustomTab
        tabs={tabs}
        value={filter}
        onChange={(_, newValue) => {
          if (
            newValue === "accepted" ||
            newValue === "ongoing" ||
            newValue === "completed" ||
            newValue === "requested" ||
            newValue === "cancelled"
          ) {
            setFilter(newValue);
          }
        }}
      />

      {filteredBookings.length === 0 && (
        <Typography sx={{ mt: 2 }}>
          {filter === "accepted"
            ? "No accepted stage sessions found."
            : filter === "ongoing"
              ? "No ongoing stage sessions found."
              : filter === "completed"
                ? "No completed stage sessions found."
                : filter === "requested"
                  ? "No requested stage sessions found."
                  : "No cancelled stage sessions found."}
        </Typography>
      )}

      <Stack spacing={2} width="100%" mt={2}>
        {filteredBookings.map((booking: StageBooking) => (
          <StageSessionCard
            key={booking.id}
            booking={booking}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default UserStageSessions;
